import React, { useCallback } from 'react';
import { ChevronLeft, Download, AlertTriangle } from 'lucide-react';

/*
 * Pipeline Snapshot display (P1c). Pure presentation over the aggregate payload
 * from reports:pipeline:snapshot:run — counts/%/$ only, no PHI. A point-in-time
 * DISTRIBUTION (current stage), not a funnel:
 *   - open-pipeline bucket table (count, % of open, open deal value) with a bar
 *   - open-pipeline headline + open-deal-value total
 *   - terminal Lost / Nurture as separate cards (excluded from open pipeline)
 *   - "N leads unmapped" footnote (current stage has no bucket row)
 * CSV export is built client-side from the returned aggregate.
 */

const card = { background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' };
const btn = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px solid #DEE2E6', background: '#fff', cursor: 'pointer', fontSize: 13, color: '#495057' };
const money = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = (n) => Number(n || 0).toLocaleString('en-US');

export default function PipelineSnapshot({ data, onBack }) {
  const exportCsv = useCallback(() => {
    if (!data) return;
    const q = (s) => `"${String(s ?? '').replace(/"/g, '""')}"`;
    const lines = [];
    lines.push(['Bucket', 'Count', '% of open pipeline', 'Open deal value'].map(q).join(','));
    for (const b of data.buckets || []) {
      lines.push([b.bucket, b.count, `${b.pctOfOpen}%`, money(b.openOppValue)].map(q).join(','));
    }
    lines.push('');
    lines.push([q('Open pipeline'), data.openPipeline].join(','));
    lines.push([q('Open deal value (total)'), money(data.openOppValueTotal)].join(','));
    for (const t of data.terminal || []) lines.push([q(`Terminal — ${t.bucket}`), t.count].join(','));
    if (data.unmapped?.count) lines.push([q('Unmapped (current stage)'), data.unmapped.count].join(','));
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'pipeline_snapshot.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  }, [data]);

  if (!data) return null;
  const { openPipeline, openOppValueTotal, buckets = [], terminal = [], unmapped } = data;
  const maxCount = buckets.reduce((m, b) => Math.max(m, b.count), 0);
  const allTime = !data.range?.from && !data.range?.to;

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <button style={btn} onClick={onBack}><ChevronLeft size={14} /> Back</button>
        <div style={{ fontWeight: 700, fontSize: 15, color: '#212529' }}>Pipeline snapshot</div>
        <span style={{ fontSize: 12, color: '#ADB5BD' }}>
          {allTime ? 'all active leads' : 'active leads created in range'} · as of {new Date(data.asOf).toLocaleString('en-US')}
        </span>
        <div style={{ marginLeft: 'auto' }}>
          <button style={btn} onClick={exportCsv}><Download size={13} /> Export CSV</button>
        </div>
      </div>

      {/* Headline */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <div style={{ ...card, flex: 1, minWidth: 180 }}>
          <div style={{ fontSize: 12, color: '#6C757D' }}>Open pipeline (excl. terminal)</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#2E8B98' }}>{num(openPipeline)}</div>
        </div>
        <div style={{ ...card, flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 12, color: '#6C757D' }}>Open deal value</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#D97706' }}>{money(openOppValueTotal)}</div>
        </div>
      </div>

      {/* Open-pipeline distribution */}
      <div style={{ ...card, marginBottom: 12 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: 'left', color: '#6C757D', fontSize: 12 }}>
              <th style={{ padding: '6px 8px' }}>Stage bucket</th>
              <th style={{ padding: '6px 8px', width: '34%' }}>Share of open pipeline</th>
              <th style={{ padding: '6px 8px', textAlign: 'right' }}>Count</th>
              <th style={{ padding: '6px 8px', textAlign: 'right' }}>% of open</th>
              <th style={{ padding: '6px 8px', textAlign: 'right' }}>Open deal value</th>
            </tr>
          </thead>
          <tbody>
            {buckets.map((b) => (
              <tr key={b.sortOrder} style={{ borderTop: '1px solid #F1F3F5' }}>
                <td style={{ padding: '8px', fontWeight: 600, color: '#212529' }}>{b.bucket}</td>
                <td style={{ padding: '8px' }}>
                  <div style={{ background: '#E7F4F6', borderRadius: 4, height: 16, width: `${maxCount ? Math.round((b.count / maxCount) * 100) : 0}%`, minWidth: b.count ? 2 : 0 }} />
                </td>
                <td style={{ padding: '8px', textAlign: 'right' }}>{num(b.count)}</td>
                <td style={{ padding: '8px', textAlign: 'right' }}>{b.pctOfOpen}%</td>
                <td style={{ padding: '8px', textAlign: 'right', color: '#6C757D' }}>{money(b.openOppValue)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Terminal side-states (excluded from open pipeline) */}
      {terminal.length > 0 && (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
          {terminal.map((t) => (
            <div key={t.bucket} style={{ ...card, flex: 1, minWidth: 160 }}>
              <div style={{ fontSize: 12, color: '#6C757D' }}>{t.bucket} (terminal — not in open pipeline)</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: t.bucket === 'Lost' ? '#DC2626' : '#D97706' }}>{num(t.count)}</div>
            </div>
          ))}
        </div>
      )}

      {/* Unmapped footnote */}
      {unmapped?.count > 0 && (
        <div style={{ ...card, background: '#FDECEA', border: '1px solid #F5C6C0', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <AlertTriangle size={18} color="#C0392B" style={{ flexShrink: 0, marginTop: 1 }} />
          <div style={{ fontSize: 12, color: '#7B241C', lineHeight: 1.5 }}>
            <strong>{num(unmapped.count)} active lead{unmapped.count === 1 ? '' : 's'} unmapped.</strong> Their current
            stage has no funnel-bucket mapping, so they're excluded from the buckets above:{' '}
            {unmapped.stages.map((s, i) => (
              <span key={s.stage}>{i > 0 ? ', ' : ''}<code>{s.stage}</code> ({s.count})</span>
            ))}
            . Add these to the bucket map to include them.
          </div>
        </div>
      )}
    </div>
  );
}
